import React, { useCallback } from 'react';
import { ThumbsUp, ThumbsDown, HelpCircle, Laugh } from 'lucide-react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import { hasUserReacted } from '../../../utils/messageUtils';
import type { ReactionType, MessageReactions, Activity } from '../../../types/activities';

interface ReactionPickerProps {
  currentUserId: string | undefined;
  existingReactions?: MessageReactions;
  onReaction: (reaction: ReactionType) => void;
  message?: Activity;
}

const REACTION_OPTIONS: { type: ReactionType; label: string; icon: React.ReactNode }[] = [
  { type: 'thumbs_up', label: '👍', icon: <ThumbsUp className="h-3 w-3" /> },
  { type: 'thumbs_down', label: '👎', icon: <ThumbsDown className="h-3 w-3" /> },
  { type: 'question', label: '❓', icon: <HelpCircle className="h-3 w-3" /> },
  { type: 'laugh', label: '😂', icon: <Laugh className="h-3 w-3" /> },
];

export function ReactionPicker({ currentUserId, existingReactions, onReaction, message }: ReactionPickerProps) {
  const handleReactionClick = useCallback(
    (reaction: ReactionType) => {
      onReaction(reaction);
    },
    [onReaction]
  );

  const hasReacted = useCallback(
    (reaction: ReactionType) => {
      if (message) {
        return hasUserReacted(message, reaction, currentUserId);
      }
      if (!currentUserId || !existingReactions) return false;
      const userIds = existingReactions[reaction];
      if (!userIds) return false;
      return userIds.includes(currentUserId);
    },
    [currentUserId, existingReactions, message]
  );

  return (
    <div className="flex items-center gap-1 bg-background border rounded-md px-1 py-1 shadow-sm">
      {REACTION_OPTIONS.map(({ type, label }) => {
        const reacted = hasReacted(type);
        return (
          <Button
            key={type}
            variant={reacted ? 'default' : 'ghost'}
            size="sm"
            className={cn(
              'h-7 w-7 p-0',
              reacted && 'bg-primary text-primary-foreground'
            )}
            onClick={() => handleReactionClick(type)}
            aria-label={`React with ${type}`}
            title={`React with ${label}`}
          >
            <span className="text-xs">{label}</span>
          </Button>
        );
      })}
    </div>
  );
}

